import { GPS, TrapSite } from "../../../types";
import { distanceToMeters, haversineMeters } from "../../../utils/geo";
import { DEFAULT_TRAPSITE_RADIUS_M } from "./constants";

export interface NearbyTrapSite {
  id: string;
  displayName: string;
  gps: GPS;
  distanceMeters: number;
}

/** A location's own reach in metres, or the default when it does not say. */
export const radiusOf = (site: Partial<TrapSite>): number =>
  typeof site.radius_value === "number" && site.radius_value > 0
    ? distanceToMeters(site.radius_value, site.radius_unit ?? "m")
    : DEFAULT_TRAPSITE_RADIUS_M;

/** The location the trap is currently assigned to, for the warning's wording. */
export interface CurrentTrapSite {
  displayName: string;
  radiusMeters: number;
}

export type TrapProximity =
  /** No site chosen yet, or the chosen site has no position to compare with. */
  | { kind: "unknown" }
  /** Comfortably inside its site. Nothing to say. */
  | { kind: "ok"; distanceMeters: number }
  /**
   * Outside its own site, but inside another one's radius.
   *
   * Note "covered by", not "nearer": a location two kilometres away with a
   * wide radius can contain a trap that a location down the road does not,
   * and calling that one "closer" would be plainly false.
   */
  | {
      kind: "far-but-covered";
      distanceMeters: number;
      current: CurrentTrapSite;
      covering: NearbyTrapSite;
    }
  /** Outside its own site and inside no other. */
  | { kind: "stranded"; distanceMeters: number; current: CurrentTrapSite };

const positioned = (
  site: Partial<TrapSite>,
): site is Partial<TrapSite> & { _id: string; gps: GPS } =>
  !!site._id &&
  typeof site.gps?.lat === "number" &&
  typeof site.gps?.lon === "number";

/**
 * How a trap's position relates to the collection location it belongs to.
 *
 * "Too far" means outside that location's own radius, so a park covering a
 * square mile and a rooftop covering fifty metres each get judged on their own
 * terms rather than against one global number.
 *
 * A trap sitting outside its location is usually one of two mistakes: it is
 * assigned to the wrong one, or it belongs somewhere that has no location yet.
 * Distinguishing them is the point - the first is a correction the user can
 * accept in one click, the second needs a new location creating.
 */
export const assessTrapProximity = (
  gps: GPS,
  trapSiteId: string | null,
  trapSites: Partial<TrapSite>[],
): TrapProximity => {
  const sites = trapSites.filter(positioned);
  const current = trapSiteId
    ? sites.find((s) => s._id === trapSiteId)
    : undefined;

  // Nothing to measure against: an unassigned trap is not "far" from anything.
  if (!current) return { kind: "unknown" };

  const distanceMeters = haversineMeters(gps, current.gps);
  const currentInfo: CurrentTrapSite = {
    displayName: current.display_name || "its collection location",
    radiusMeters: radiusOf(current),
  };
  if (distanceMeters <= currentInfo.radiusMeters) {
    return { kind: "ok", distanceMeters };
  }

  // A neighbour is worth suggesting when the trap falls inside ITS radius -
  // the question is which location actually contains the trap, not which pin
  // happens to be nearest.
  const others = sites
    .filter((s) => s._id !== current._id)
    .map((s) => ({
      id: s._id,
      displayName: s.display_name || "Unnamed location",
      gps: s.gps,
      distanceMeters: haversineMeters(gps, s.gps),
      radius: radiusOf(s),
    }))
    .filter((s) => s.distanceMeters <= s.radius)
    .sort((a, b) => a.distanceMeters - b.distanceMeters);

  return others.length > 0
    ? {
        kind: "far-but-covered",
        distanceMeters,
        current: currentInfo,
        covering: others[0],
      }
    : { kind: "stranded", distanceMeters, current: currentInfo };
};

/**
 * A position for a trap that has none, placed just off the location it is
 * being added to.
 *
 * Offset rather than exact so it does not hide under the location's own pin -
 * the whole point is that it is visible and draggable to where the trap
 * actually is. Roughly 30m north-east, which is a pin's width at the zoom the
 * editor opens at.
 */
export const nearbyPlacement = (origin: GPS, index = 0): GPS => {
  const step = 0.0003; // ~33m of latitude
  // Fan successive traps out rather than stacking them on one point.
  const angle = (index * 2 * Math.PI) / 8 + Math.PI / 4;
  return {
    lat: origin.lat + step * Math.sin(angle),
    lon:
      origin.lon +
      (step * Math.cos(angle)) /
        Math.max(Math.cos((origin.lat * Math.PI) / 180), 0.01),
  };
};
